import { useEffect, useRef } from 'react';
import type { PlayerView } from '@il-colpo/engine';
import type { GameClient } from '../game/client.ts';
import { track } from './analytics.ts';

/**
 * Tells analytics that a heist began and how it ended: the mode, the map and difficulty the game was set
 * up with, whether it succeeded and about how many minutes it took. Once each per game, and not for a
 * replay (that is a heist watched again, not played).
 */
export function useHeistAnalytics(client: GameClient, view: PlayerView): void {
  const mode = client.mode;
  const map = view.game.mapId;
  const difficulty = view.game.difficulty;
  const success = view.result?.success ?? null;
  const minutes = Math.round(view.game.clockMs / 60_000);
  const started = useRef<GameClient | null>(null);
  const ended = useRef<GameClient | null>(null);

  useEffect(() => {
    if (mode === 'replay' || started.current === client) return;
    started.current = client;
    track('heist_started', { mode, map, difficulty });
  }, [client, mode, map, difficulty]);

  useEffect(() => {
    if (mode === 'replay' || success === null || ended.current === client) return;
    ended.current = client;
    track('heist_ended', { mode, success, minutes });
  }, [client, mode, success, minutes]);
}
